import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Calendar, 
  CheckCircle2, 
  ChevronDown, 
  Compass, 
  Flag, 
  GraduationCap,
  Heart, 
  Info, 
  Map, 
  Medal, 
  Star, 
  Target, 
  Users,
  Tent,
  Navigation,
  Shield,
  Globe2 as Globe,
  AlertTriangle,
  Monitor
} from 'lucide-react';
import { useTranslation } from '../Context/TranslationContext';

const ScoutSection = () => {
  const { isTamil } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);

  const overviewContent = {
    intro: {
      en: "The Scout section is designed for boys aged 10+ to 17 years, focusing on developing leadership, outdoor skills, and character through advanced scouting activities and community service.",
      ta: "சாரணர் பிரிவு 10+ முதல் 17 வயது வரையிலான சிறுவர்களுக்காக வடிவமைக்கப்பட்டுள்ளது, மேம்பட்ட சாரணர் செயல்பாடுகள் மற்றும் சமூக சேவை மூலம் தலைமைத்துவம், வெளிப்புற திறன்கள் மற்றும் குணாதிசயத்தை வளர்ப்பதில் கவனம் செலுத்துகிறது."
    },
    keyFeatures: [
      {
        title: { en: "Age Group", ta: "வயது பிரிவு" },
        content: { en: "10+ to 17 years", ta: "10+ முதல் 17 வயது வரை" },
        icon: Users
      },
      {
        title: { en: "Unit Name", ta: "அலகு பெயர்" },
        content: { en: "Scout Troop", ta: "சாரணர் படை" },
        icon: Flag
      },
      {
        title: { en: "Motto", ta: "குறிக்கோள்" },
        content: { en: "Be Prepared", ta: "தயாராக இருங்கள்" },
        icon: Target
      }
    ],
    objectives: [
      {
        title: { en: "Leadership Development", ta: "தலைமைத்துவ வளர்ச்சி" },
        content: { 
          en: "Building leadership skills through patrol system and progressive responsibility",
          ta: "ரோந்து முறை மற்றும் முன்னேற்ற பொறுப்புகள் மூலம் தலைமைத்துவ திறன்களை வளர்த்தல்"
        }
      },
      {
        title: { en: "Advanced Scouting", ta: "மேம்பட்ட சாரணர் பயிற்சி" },
        content: {
          en: "Mastering outdoor skills, camping, pioneering, and navigation",
          ta: "வெளிப்புற திறன்கள், முகாமிடல், முன்னோடி பணி, மற்றும் வழிசெலுத்தல் ஆகியவற்றில் தேர்ச்சி"
        }
      },
      {
        title: { en: "Community Service", ta: "சமூக சேவை" },
        content: {
          en: "Engaging in meaningful community service projects and social development",
          ta: "அர்த்தமுள்ள சமூக சேவை திட்டங்கள் மற்றும் சமூக மேம்பாட்டில் ஈடுபடுதல்"
        }
      }
    ]
  };

  const sectionTitles = {
    overview: {
      title: { en: "Overview", ta: "கண்ணோட்டம்" },
    },
    advancement: {
      title: { en: "Advancement Scheme", ta: "முன்னேற்றத் திட்டம்" },
    },
    proficiencyBadges: {
      title: { en: "Proficiency Badges", ta: "திறன் பதக்கங்கள்" },
    }
  };

  const advancementScheme = [
    {
      title: { en: "Pravesh", ta: "பிரவேஷ்" },
      duration: { en: "3 months", ta: "3 மாதங்கள்" },
      requirements: [
        { en: "Know Scout Promise, Law & Motto", ta: "சாரணர் வாக்குறுதி, சட்டம் & குறிக்கோள்" },
        { en: "Know Scout Sign, Salute and Handshake", ta: "சாரணர் சைகை, வணக்கம் மற்றும் கைகுலுக்கல்" },
        { en: "Knowledge about Flags and Flag Etiquette", ta: "கொடிகள் மற்றும் கொடி மரியாதை பற்றிய அறிவு" },
        { en: "Attend four Troop Meetings", ta: "நான்கு படை கூட்டங்களில் கலந்துகொள்ளுதல்" }
      ],
      icon: Flag
    },
    {
      title: { en: "Pratham Sopan", ta: "பிரதம சோபான்" },
      duration: { en: "6 months", ta: "6 மாதங்கள்" },
      requirements: [
        { en: "Personal fitness and discipline", ta: "தனிப்பட்ட உடற்தகுதி மற்றும் ஒழுக்கம்" },
        { en: "Basic pioneering skills", ta: "அடிப்படை முன்னோடி திறன்கள்" },
        { en: "First aid and safety", ta: "முதலுதவி மற்றும் பாதுகாப்பு" },
        { en: "Communication skills", ta: "தொடர்பு திறன்கள்" }
      ],
      icon: Star
    },
    {
      title: { en: "Dwitiya Sopan", ta: "த்விதீய சோபான்" },
      duration: { en: "6 months", ta: "6 மாதங்கள்" },
      requirements: [
        { en: "Advanced pioneering", ta: "மேம்பட்ட முன்னோடி பணி" },
        { en: "Fire and cooking", ta: "நெருப்பு மற்றும் சமையல்" },
        { en: "Map and compass", ta: "வரைபடம் மற்றும் திசைகாட்டி" },
        { en: "Estimation and first aid", ta: "மதிப்பீடு மற்றும் முதலுதவி" }
      ],
      icon: Compass
    },
    {
      title: { en: "Tritiya Sopan", ta: "திரிதீய சோபான்" },
      duration: { en: "6 months", ta: "6 மாதங்கள்" },
      requirements: [
        { en: "Advanced knots and lashings", ta: "மேம்பட்ட முடிச்சுகள் மற்றும் கட்டுகள்" },
        { en: "Swimming and lifesaving", ta: "நீச்சல் மற்றும் உயிர்காப்பு" },
        { en: "Mapping skills", ta: "வரைபட திறன்கள்" },
        { en: "Public speaking", ta: "பொது பேச்சு" }
      ],
      icon: Medal
    },
    {
      title: { en: "Rajya Puraskar", ta: "ராஜ்ய புரஸ்கார்" },
      duration: { en: "9 months", ta: "9 மாதங்கள்" },
      requirements: [
        { en: "Hold Tritiya Sopan", ta: "திரிதீய சோபான் பெற்றிருத்தல்" },
        { en: "Ambulance Badge", ta: "ஆம்புலன்ஸ் பதக்கம்" },
        { en: "Overnight hiking", ta: "இரவு நடைபயணம்" },
        { en: "Nature projects", ta: "இயற்கை திட்டங்கள்" }
      ],
      icon: Award
    },
    {
      title: { en: "Rashtrapati Scout", ta: "ராஷ்டிரபதி சாரணர்" },
      special: true,
      duration: { en: "12 months", ta: "12 மாதங்கள்" },
      requirements: [
        { en: "Hold Rajya Puraskar", ta: "ராஜ்ய புரஸ்கார் பெற்றிருத்தல்" },
        { en: "Community development project", ta: "சமூக மேம்பாட்டு திட்டம்" },
        { en: "Special proficiency badges", ta: "சிறப்பு திறன் பதக்கங்கள்" },
        { en: "Advanced camping skills", ta: "மேம்பட்ட முகாமிடல் திறன்கள்" }
      ],
      icon: Award
    }
  ];

  const proficiencyBadges = [
    {
      category: { en: "Outdoor Skills", ta: "வெளிப்புற திறன்கள்" },
      badges: [
        { name: { en: "Camper", ta: "முகாமாளர்" }, icon: Tent },
        { name: { en: "Pioneer", ta: "முன்னோடி" }, icon: Map },
        { name: { en: "Navigator", ta: "வழிகாட்டி" }, icon: Navigation }
      ]
    },
    {
      category: { en: "Service Badges", ta: "சேவை பதக்கங்கள்" },
      badges: [
        { name: { en: "Ambulance Man", ta: "ஆம்புலன்ஸ் மேன்" }, icon: Heart },
        { name: { en: "Public Health", ta: "பொது சுகாதாரம்" }, icon: Users },
        { name: { en: "Civil Defence", ta: "சிவில் பாதுகாப்பு" }, icon: Shield }
      ]
    },
    {
      category: { en: "Special Skills", ta: "சிறப்பு திறன்கள்" },
      badges: [
        { name: { en: "World Conservation", ta: "உலக பாதுகாப்பு" }, icon: Globe },
        { name: { en: "Disaster Management", ta: "பேரிடர் மேலாண்மை" }, icon: AlertTriangle },
        { name: { en: "Computer Awareness", ta: "கணினி விழிப்புணர்வு" }, icon: Monitor }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 mt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2C251D] to-[#8A7968] py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-bold mb-6">
              {isTamil ? "சாரணர் பிரிவு" : "Scout Section"}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {overviewContent.intro[isTamil ? 'ta' : 'en']}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Overview Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.overview.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#8A7968] mx-auto"></div>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {overviewContent.keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-[#8A7968] bg-opacity-10 rounded-lg text-[#8A7968]">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold">
                    {feature.title[isTamil ? 'ta' : 'en']}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {feature.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>

      {/* Objectives */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {overviewContent.objectives.map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-xl font-semibold mb-4">
                  {objective.title[isTamil ? 'ta' : 'en']}
                </h3>
                <p className="text-gray-600">
                  {objective.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Advancement Section */}
        <section className="py-16 bg-white">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.advancement.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#8A7968] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advancementScheme.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl shadow-lg p-6 ${level.special ? 'border-2 border-yellow-500' : ''}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-lg ${level.special ? 'bg-yellow-50 text-yellow-600' : 'bg-[#8A7968] bg-opacity-10 text-[#8A7968]'}`}>
                    <level.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {level.title[isTamil ? 'ta' : 'en']}
                    </h3>
                    {level.duration && (
                      <p className="text-sm text-gray-500">
                        {level.duration[isTamil ? 'ta' : 'en']}
                      </p>
                    )}
                  </div>
                </div>
                <ul className="space-y-2">
                  {level.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {req[isTamil ? 'ta' : 'en']}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Proficiency Badges Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.proficiencyBadges.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#8A7968] mx-auto"></div>
          </div>

          <div className="space-y-8">
            {proficiencyBadges.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-semibold mb-6">
                  {category.category[isTamil ? 'ta' : 'en']}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {category.badges.map((badge, bIndex) => (
                    <div key={bIndex} className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                      <div className="p-2 bg-[#8A7968] bg-opacity-10 rounded-lg text-[#8A7968]">
                        <badge.icon className="w-5 h-5" />
                      </div>
                      <span className="text-gray-700">
                        {badge.name[isTamil ? 'ta' : 'en']}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ScoutSection;